import type { PublicRoomView } from "@secret-cabinet/contracts";
import { Check, Copy, ExternalLink, QrCode as QrIcon, ScrollText } from "lucide-react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";

export function HistoryPanel({ room }: { room: PublicRoomView }) {
  return (
    <section className="record-panel">
      <header className="section-heading">
        <span><ScrollText aria-hidden="true" /> Chamber record</span>
        <b>{room.history.length} entries</b>
      </header>
      {room.history.length ? (
        <ol className="history-list">
          {[...room.history].reverse().map((entry) => (
            <li key={entry.id} className={entry.policy ? `history-policy history-policy--${entry.policy}` : ""}>
              <time dateTime={new Date(entry.at).toISOString()}>{formatTime(entry.at)}</time>
              <span>{entry.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="empty-copy">The clerk has not entered any business yet.</p>
      )}
    </section>
  );
}

export function QrPanel({ code, tv = false, title, instruction }: { code: string; tv?: boolean; title?: string; instruction?: string }) {
  const [copied, setCopied] = useState(false);
  const joinUrl = joinUrlFor(code);

  async function copyCode(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section className={`qr-panel${tv ? " qr-panel--tv" : ""}`}>
      <div className="qr-plate" aria-label={`QR code to join room ${code}`}>
        <QRCodeSVG
          value={joinUrl}
          size={tv ? 244 : 220}
          level="M"
          bgColor="#f6f0df"
          fgColor="#241821"
          marginSize={1}
        />
      </div>
      <div className="qr-copy">
        <p className="eyebrow"><QrIcon aria-hidden="true" /> Scan to join</p>
        <div className="join-code">{code}</div>
        {title ? <h1>{title}</h1> : null}
        <p>{instruction ?? "Keep this shared screen open for the public chamber. Open the room on one device per player; roles and decisions stay on that device."}</p>
        {!tv ? (
          <>
            <button className="button button--ink" type="button" onClick={() => void copyCode()}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? "Code copied" : "Copy room code"}
            </button>
            <a className="button button--quiet" href={`${joinUrl}&tv=1`} target="_blank" rel="noreferrer">
              Open public TV <ExternalLink aria-hidden="true" />
            </a>
          </>
        ) : null}
      </div>
    </section>
  );
}

export function RulesPanel() {
  return (
    <article className="rules-panel">
      <header className="section-heading">
        <span>Clerk's rulebook</span>
        <b>Base game</b>
      </header>
      <section>
        <h2>Win the government</h2>
        <p><b>Liberals</b> win with five Liberal policies or by executing Hitler.</p>
        <p><b>Fascists</b> win with six Fascist policies, or if Hitler is elected Chancellor after three Fascist policies are already enacted.</p>
      </section>
      <section>
        <h2>Run an election</h2>
        <p>The Presidency moves clockwise. The President nominates an eligible Chancellor; every living player votes Ja or Nein. A strict majority is required.</p>
        <p>The previous Chancellor is ineligible. The previous President is also ineligible while six or more players remain.</p>
      </section>
      <section>
        <h2>Pass a policy</h2>
        <p>The President privately discards one of three policies. The Chancellor privately discards one of the remaining two, enacting the last. Never show or describe the hand while deciding.</p>
      </section>
      <section>
        <h2>Watch for chaos</h2>
        <p>Each failed government advances the election tracker. On the third failure, the top policy is enacted, its power is ignored, and term limits clear.</p>
      </section>
      <section>
        <h2>Presidential powers</h2>
        <p>Fascist policies may grant an investigation, policy peek, special election, or execution. The exact track changes with player count and is printed on the TV.</p>
      </section>
      <section>
        <h2>Table conduct</h2>
        <p>Talk, accuse, promise, and lie. Keep your screen private. Executed players leave the discussion and may not reveal their role.</p>
      </section>
      <footer>
        This noncommercial adaptation follows the official Secret Hitler base rules. Original game design by Max Temkin, Mike Boxleiter, and Tommy Maranges. Licensed CC BY-NC-SA 4.0.
      </footer>
    </article>
  );
}

function joinUrlFor(code: string): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("room", code);
  return url.toString();
}

function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}
